import styled, { css } from 'styled-components';
import { CallingToolbarIconButton } from '../../external-contrib/calling-remote/components';
import { GlobalSearch } from '../../external-contrib/global-search/components';
import { NotificationsButton } from '../../external-contrib/notifications/components/NotificationsButton';
import { NavComponentsUnseenCountProvider } from '../../external-contrib/notifications/components/unseenCount';
import { UpgradeButton } from '../../external-contrib/upgrade-button/components';
import { useAppSelector } from '../../hooks';
import useMedia from '../../hooks/useMedia';
import { useIsHubless, usePortal, useUser } from '../../hooks/useNavConfig';
import useRemoveNavPlaceholder from '../../hooks/useRemoveNavPlaceholder';
import { useSettingsLink } from '../../hooks/useSettingsLink';
import { isGlobalNavAcademyMode } from '../../utils/navEnviro';
import { Accounts } from '../Accounts';
import { ButtonWithDemoExitConfirmation } from '../DemoExitConfirmation/ButtonWithDemoExitConfirmation';
import ErrorBoundary from '../ErrorBoundary';
import { HelpButtonContainer } from '../../external-contrib/in-app-help/components';
import { Menu } from '../Menu';
import { ObjectCreateButton } from '../ObjectCreateButton';
import { ThemeSwitcherButton } from '../../external-contrib/theme-switcher';
import { ActivateCopilotButton, GlobalToolbarDivider, GlobalToolbarLink, ICON_NAMES } from './Components';
import { ActivateMarketplaceNavMenuButton } from './Components/ActivateMarketplaceNavMenuButton';
import { GlobalToolbarHeader } from './GlobalToolbarHeader';
import ToolbarItem from './Components/ToolbarItem';
import { useIsGlobalToolbarExcludedRoute } from './hooks/useIsGlobalToolbarExcludedRoute';
import { StartTrialButton } from '../../external-contrib/growth-trials/components';
import { selectHasObjBuilderMenuItems } from '../../selectors';
import { selectIsCopilotEnabled } from '../../selectors/copilot.selectors';
import { useIsUngated } from '../../hooks/useIsUngated';
import { RESPONSIVE_TOOLBAR } from '../../constants/gates';
import { selectInAppHelpState } from '../../external-contrib/in-app-help/selectors';
import { BREAKPOINT_SMALL } from 'nav-meta/constants';
import { GlobalToolbarSprocket } from './Components/GlobalToolbarSprocket';
import isQA from '../../utils/isQA';
import { MiniTrialGuideContainer } from '../../external-contrib/growth-trials/components/MiniTrialGuideContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SearchAndPlusCreateWrapper = styled.div.withConfig({
  displayName: "GlobalToolbar__SearchAndPlusCreateWrapper",
  componentId: "g9am2v-0"
})(["display:flex;flex-grow:1;"]);
const PlusCreateWrapper = styled.div.withConfig({
  displayName: "GlobalToolbar__PlusCreateWrapper",
  componentId: "g9am2v-1"
})(["display:flex;flex-grow:1;padding:0 12px !important;"]);
const newListItemStyles = css(["@media screen and (max-width:554px){display:none;}"]);
const accountMenuStyles = css(["margin:0 0 0 11px !important;& > button,& > .highlight-container > button{padding:7px 8px !important;}@media screen and (max-width:924px){margin:0 !important;& > button{margin:0;}&::before{display:none;}}"]);
const StyledToolMenu = styled(Menu).withConfig({
  displayName: "GlobalToolbar__StyledToolMenu",
  componentId: "g9am2v-2"
})(["list-style-type:none;margin-bottom:0;padding-inline-start:0;display:flex;align-items:center;height:100%;justify-content:center;"]);
export default function GlobalToolbar() {
  const shouldShowToolbar = useAppSelector(state => state.overlayDisplayConfigState.shouldShowToolbar);
  const {
    isGlobaltoolbarExcludedRoute
  } = useIsGlobalToolbarExcludedRoute();
  const {
    shouldShowHelpInToolbar
  } = useAppSelector(selectInAppHelpState);
  const isGridBreakpointSmall = useMedia(`screen and (max-width: ${BREAKPOINT_SMALL})`);
  const isQa = isQA();
  const isHubless = useIsHubless();
  const hasPlusCreateItems = useAppSelector(selectHasObjBuilderMenuItems);
  const {
    firstName,
    lastName,
    displayName,
    email: userEmail,
    avatarUri: avatarUrl,
    scopes,
    enrolledInUserGuide
  } = useUser();
  const {
    hubAccountNameOrDomain: accountName,
    portalType
  } = usePortal();
  const isAcademyMode = isGlobalNavAcademyMode();
  const isHubspotter = userEmail.endsWith('@hubspot.com');
  const isDevPortal = portalType === 'DEV';
  const isEnrolledInOnboarding = enrolledInUserGuide || false;
  const hasProjectAccess = scopes.includes('projects-access');
  const isCopilotEnabledInNavConfig = useAppSelector(selectIsCopilotEnabled);
  const responsiveToolbarEnabled = useIsUngated(RESPONSIVE_TOOLBAR);
  const settingsUrl = useSettingsLink();
  useRemoveNavPlaceholder({
    placeholderType: 'global-toolbar',
    shouldRemovePlaceholder: !isGlobalNavAcademyMode()
  });
  if (!shouldShowToolbar || isGlobaltoolbarExcludedRoute) {
    return null;
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "Global Toolbar",
    children: /*#__PURE__*/_jsxs(GlobalToolbarHeader, {
      children: [/*#__PURE__*/_jsx(GlobalToolbarSprocket, {
        isHubless: isHubless,
        isGridBreakpointSmall: isGridBreakpointSmall
      }), /*#__PURE__*/_jsxs(SearchAndPlusCreateWrapper, {
        children: [/*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "Global Search",
          children: /*#__PURE__*/_jsx(GlobalSearch, {
            shouldDisplay: !isHubless
          })
        }), isGridBreakpointSmall && responsiveToolbarEnabled || !hasPlusCreateItems ? null : /*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "Global Toolbar - Object Create",
          children: /*#__PURE__*/_jsx(PlusCreateWrapper, {
            children: /*#__PURE__*/_jsx(ObjectCreateButton, {})
          })
        })]
      }), /*#__PURE__*/_jsx("nav", {
        className: "global-toolbar-items-container",
        "aria-label": "Global toolbar",
        children: /*#__PURE__*/_jsxs(StyledToolMenu, {
          id: "hs-global-toolbar-icons",
          "data-test-id": "hs-global-toolbar-icons",
          menuOrientation: "horizontal",
          children: [isHubless ? null : /*#__PURE__*/_jsxs(_Fragment, {
            children: [isQa && isHubspotter ? /*#__PURE__*/_jsx(ErrorBoundary, {
              boundaryName: "Trellis Theme Switcher",
              children: /*#__PURE__*/_jsx(ThemeSwitcherButton, {})
            }) : null, /*#__PURE__*/_jsx(ErrorBoundary, {
              boundaryName: "Upgrade Button",
              children: /*#__PURE__*/_jsx(UpgradeButton, {
                shouldDisplay: !isHubless
              })
            }), /*#__PURE__*/_jsx(ErrorBoundary, {
              boundaryName: "Start Trial Button",
              children: /*#__PURE__*/_jsx(StartTrialButton, {})
            }), /*#__PURE__*/_jsx(ErrorBoundary, {
              boundaryName: "Calling Remote Button",
              children: /*#__PURE__*/_jsx(CallingToolbarIconButton, {})
            }), /*#__PURE__*/_jsx(ButtonWithDemoExitConfirmation, {
              component: ActivateMarketplaceNavMenuButton,
              ariaLabelKey: "nav.icons.marketplace",
              iconTitleKey: "nav.icons.marketplace"
            }), /*#__PURE__*/_jsx(ErrorBoundary, {
              boundaryName: "In-App Help Button",
              children: shouldShowHelpInToolbar ? /*#__PURE__*/_jsx(ToolbarItem, {
                id: "hs-global-toolbar-help-list-item",
                render: ({
                  setRef,
                  onMenuItemKeyDown,
                  menuItemId
                }) => /*#__PURE__*/_jsx(HelpButtonContainer, {
                  ref: setRef,
                  onKeyDown: onMenuItemKeyDown,
                  id: menuItemId
                })
              }) : null
            }), /*#__PURE__*/_jsx(ButtonWithDemoExitConfirmation, {
              component: GlobalToolbarLink,
              shouldRender: !(isAcademyMode && isGridBreakpointSmall),
              id: "hs-global-toolbar-settings-list-item",
              iconName: ICON_NAMES.SETTINGS,
              itemName: "Settings",
              href: settingsUrl,
              ariaLabelKey: "nav.icons.settings",
              iconTitleKey: "nav.icons.settings",
              additionalListItemStyles: newListItemStyles
            }), /*#__PURE__*/_jsx(ErrorBoundary, {
              boundaryName: "Notifications Button",
              children: /*#__PURE__*/_jsx(NavComponentsUnseenCountProvider, {
                children: /*#__PURE__*/_jsx(NotificationsButton, {})
              })
            })]
          }), isCopilotEnabledInNavConfig && /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "Activate Copilot Button",
            children: /*#__PURE__*/_jsx(ButtonWithDemoExitConfirmation, {
              component: ActivateCopilotButton
            })
          }), !isHubless ? /*#__PURE__*/_jsx(GlobalToolbarDivider, {}) : null, /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "Mini Trial Guide Container",
            children: /*#__PURE__*/_jsx(MiniTrialGuideContainer, {})
          }), /*#__PURE__*/_jsx(ToolbarItem, {
            id: "hs-global-toolbar-accounts-list-item",
            portalHook: "account-menu",
            dataOnboardingHook: "opt-out",
            additionalStyles: accountMenuStyles,
            render: ({
              setRef,
              onMenuItemKeyDown
            }) => /*#__PURE__*/_jsx(Accounts, {
              id: "hs-global-toolbar-accounts",
              avatarUrl: avatarUrl,
              accountName: accountName,
              firstName: firstName,
              lastName: lastName,
              displayName: displayName,
              userEmail: userEmail,
              scopes: scopes,
              isDevPortal: isDevPortal,
              hasProjectAccess: hasProjectAccess,
              isEnrolledInOnboarding: isEnrolledInOnboarding,
              popoverPadding: 0,
              ref: setRef,
              onKeyDown: onMenuItemKeyDown
            })
          })]
        })
      })]
    })
  });
}